import type { Profile } from '@keboola/api-client/developerPortal/types';

export type MfaStatus = {
  /** What the profile page states the current enrolment is. */
  label: string;
  /** SMS is still accepted by Cognito but must be actively discouraged. */
  isDeprecated: boolean;
  /** Text for the enrolment button — re-enrolling an app is "changing device", not "setting up". */
  actionLabel: string;
};

/**
 * Describes an `mfa` value from `GET /auth/profile`.
 *
 * A pure function because the three cases carry real consequences and each deserves a test: `false` means
 * the user is walled out of the dashboard entirely, `'SMS_MFA'` means they are let in but should be nudged
 * off it, and `'SOFTWARE_TOKEN_MFA'` means the only reason to come here is a new phone.
 */
export const describeMfa = (mfa: Profile['mfa'] | undefined): MfaStatus => {
  if (mfa === 'SOFTWARE_TOKEN_MFA') {
    return { label: 'Enabled', isDeprecated: false, actionLabel: 'Change device' };
  }
  if (mfa === 'SMS_MFA') {
    return { label: 'Enabled (SMS)', isDeprecated: true, actionLabel: 'Set up using an app' };
  }
  // `undefined` (still loading) is grouped with `false` for the *label* only — the page shows the button
  // either way, and offering setup to someone already enrolled is harmless where claiming "Disabled"
  // about them would not be. Callers render the label behind their own loading state.
  return { label: 'Disabled', isDeprecated: false, actionLabel: 'Set up using an app' };
};
